import React, { useState, useEffect } from 'react';
import { 
  X, 
  CreditCard, 
  Heart,
  Activity,
  Pill,
  FileText,
  Baby,
  Stethoscope,
  AlertCircle,
  Cpu,
  IndianRupee,
  ChevronRight
} from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

const ServicesPricingModal = ({ isOpen, onClose }) => {
  const [servicesData, setServicesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services');

  useEffect(() => {
    if (isOpen) {
      fetchServicesPricing();
    }
  }, [isOpen]);

  const fetchServicesPricing = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/backend/api/services-pricing');
      const data = await response.json();
      
      if (data.success) {
        setServicesData(data.data);
      }
    } catch (error) {
      console.error('Error fetching services and pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (iconName) => {
    const icons = {
      consultation: <Stethoscope className="w-6 h-6" />,
      speciality: <Activity className="w-6 h-6" />,
      health_check: <Heart className="w-6 h-6" />,
      pharmacy: <Pill className="w-6 h-6" />,
      lab: <FileText className="w-6 h-6" />,
      physiotherapy: <Activity className="w-6 h-6" />,
      diagnostics: <Cpu className="w-6 h-6" />,
      maternity: <Baby className="w-6 h-6" />,
      cosmetics: <Heart className="w-6 h-6" />,
      urgent: <AlertCircle className="w-6 h-6" />,
      surgery: <Activity className="w-6 h-6" />
    };
    return icons[iconName] || <Heart className="w-6 h-6" />;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN').format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CreditCard className="w-6 h-6" />
              <h2 className="text-xl font-semibold">Services & Pricing</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex space-x-1 p-1">
            <button
              onClick={() => setActiveTab('services')}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'services' 
                  ? 'bg-white text-red-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Our Services
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'pricing' 
                  ? 'bg-white text-red-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Pricing Details
            </button>
            <button
              onClick={() => setActiveTab('technology')}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'technology' 
                  ? 'bg-white text-red-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Advanced Technology
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading services and pricing...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Services Tab */}
              {activeTab === 'services' && servicesData && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Additional Services & Expertise
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {servicesData.additional_services.map((service, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="text-red-600 group-hover:scale-110 transition-transform">
                            {getServiceIcon(service.icon)}
                          </div>
                          <span className="font-medium text-gray-700">{service.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && servicesData && (
                <div className="space-y-8">
                  {/* Consultation Fees */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Stethoscope className="w-5 h-5 mr-2 text-red-600" />
                      Doctor Consultation Fees
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        {servicesData.pricing.consultation_fees.map((fee, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg">
                            <span className="font-medium text-gray-700">{fee.type}</span>
                            <div className="flex items-center space-x-1 text-red-600 font-semibold">
                              <IndianRupee className="w-4 h-4" />
                              <span>{formatPrice(fee.min_price)} - {formatPrice(fee.max_price)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Diagnostic Services */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Cpu className="w-5 h-5 mr-2 text-red-600" />
                      Diagnostic Services
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        {servicesData.pricing.diagnostic_services.map((service, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg">
                            <div>
                              <span className="font-medium text-gray-700">{service.service}</span>
                              {service.unit && (
                                <span className="text-sm text-gray-500 ml-2">({service.unit})</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-1 text-red-600 font-semibold">
                              <IndianRupee className="w-4 h-4" />
                              <span>{formatPrice(service.min_price)} - {formatPrice(service.max_price)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Surgical Procedures */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Activity className="w-5 h-5 mr-2 text-red-600" />
                      Surgical Procedures
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        {servicesData.pricing.surgical_procedures.map((procedure, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg">
                            <span className="font-medium text-gray-700">{procedure.type}</span>
                            <div className="flex items-center space-x-1 text-red-600 font-semibold">
                              <span className="text-sm text-gray-600 mr-2">Starting from</span>
                              <IndianRupee className="w-4 h-4" />
                              <span>{formatPrice(procedure.starting_price)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technology Tab */}
              {activeTab === 'technology' && servicesData && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Advanced Technology & Patient Care
                  </h3>
                  <div className="space-y-4">
                    {servicesData.advanced_technology.map((tech, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start space-x-3">
                          <ChevronRight className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-gray-800 mb-1">{tech.name}</h4>
                            <p className="text-gray-600 text-sm">{tech.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <p className="text-sm text-gray-600 text-center">
            * Prices are indicative and may vary based on specific requirements and conditions
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServicesPricingModal;